import { evaluateFieldVisibility } from '@mobilelive-inc/telus-forms-core';

import { getFieldValue } from './utils';
import {
  getPricingSummary,
  getVulcanPriceSelections,
  getFormFieldSummary,
  getLocalLanguageIsDifferent
} from './vulcanPricing/vulcanPricing';
import {
  existingEquipmentVulcan,
  fieldIsVulcanOrder,
  fieldProvinceVUL,
  keyControlAdditionalEquipmentVulcan,
  keyControlIncludedEquipmentCameraSelectionVulcan,
  keyControlIncludedEquipmentVulcan,
  keyMonitorAdditionalEquipmentVulcan,
  keyMonitorIncludedEquipmentVulcan,
  keyProtectAdditionalEquipmentVulcan,
  keyProtectIncludedEquipmentCameraSelectionVulcan,
  keyProtectIncludedEquipmentVulcan,
  smartCameraAdditionalEquipmentVulcan,
  smartCameraIncludedEquipmentCameraSelectionVulcan,
  takeoverEquipmentWired,
  takeoverEquipmentWireless,
  vulcanProvinces
} from '../utils/constant';

/**
 * Delegates to @mobilelive-inc/telus-forms-core's evaluateFieldVisibility
 * (SOF-3402 Vulcan migration, ARCH-706). Preserves the exact pre-migration
 * signature `(field, fields, orderData, isFormTouched)` so every call site in
 * formStepVulcan.js is unchanged — only the implementation moved.
 *
 * `fields` is the app's rich formFields record (`{ [name]: { value, isVisible,
 * checked, type, ... } }`); the package's evaluator takes flat `values`/
 * `visibility` maps, so this shim does that conversion (identical to the one
 * verified in scripts/verify-vulcan-visibility-parity.js).
 *
 * `backendFieldValidationMode: 'all'` matches Vulcan's AND semantics for
 * multiple backendFieldValidation entries — see package CHANGELOG v1.0.1 for
 * why this is configurable (WLN's variant is 'any'/OR; the two are genuinely
 * conflicting, not just superset-compatible).
 */
export const isRelatedFieldExisted = (
  field,
  fields,
  orderData,
  isFormTouched
) => {
  const values = {};
  const visibility = {};
  Object.keys(fields || {}).forEach(name => {
    const f = fields[name] || {};
    values[name] = f.type === 'Checkbox' ? f.checked : f.value;
    visibility[name] = f.isVisible;
  });

  return evaluateFieldVisibility(field, {
    values,
    visibility,
    orderData,
    isFormTouched,
    multiSelectFieldNames: [],
    backendFieldValidationMode: 'all'
  });
};

const removeUnusedSelectionsIncludedEquipments = obj =>
  Object.entries(obj)
    .filter(([, value]) => !!value)
    .reduce((acc, item) => ({ ...acc, [item[0]]: item[1] }), {});

const getEquipmentGroupFormData = obj => {
  const result = Object.keys(obj)
    .filter(
      key =>
        (obj[key] || {}).isVisible &&
        typeof (obj[key] || {}).value !== 'undefined'
    )
    .reduce((acc, key) => {
      const { value } = obj[key];
      acc[key] = value;
      return acc;
    }, {});
  return result;
};

export const getFormData = (
  fields,
  step,
  formFieldsForSummary,
  formFieldsCopy,
  redeemOfferData,
  orderData = {},
  businessOptikTvTableData,
  officePhoneLinesData,
  fibreInternetData,
  copperInternetData,
  tsbData
) => {
  const result = Object.keys(fields).reduce((acc, key) => {
    const { type, value, checked } = fields[key];

    // Handle Business Optik TV table data specially
    if (type === 'OptikTvTable' && value) {
      acc[key] = value; // Store the entire table data object
    } else {
      acc[key] = type === 'Checkbox' ? checked : value || '';
    }
    return acc;
  }, {});
  if (fields.files) {
    result.files = fields.files;
  }

  // Add office phone lines data if available
  if (officePhoneLinesData && officePhoneLinesData.length > 0) {
    result.officePhoneLinesData = officePhoneLinesData;
  }

  result.priceSelections = {
    ...result.priceSelections,
    ...getVulcanPriceSelections(
      fields,
      redeemOfferData,
      orderData,
      businessOptikTvTableData,
      officePhoneLinesData,
      fibreInternetData,
      copperInternetData,
      tsbData,
      step
    )
  };

  // We should update and refactor this logic so it becomes generic and can be
  // reused instead of handling it case by case.
  // Handling Secure Business Selections
  if (fields[keyMonitorIncludedEquipmentVulcan]) {
    result[keyMonitorIncludedEquipmentVulcan] = getEquipmentGroupFormData(
      fields[keyMonitorIncludedEquipmentVulcan]
    );
    result[
      keyMonitorIncludedEquipmentVulcan
    ] = removeUnusedSelectionsIncludedEquipments(
      result[keyMonitorIncludedEquipmentVulcan]
    );
  }
  if (fields[keyMonitorAdditionalEquipmentVulcan]) {
    result[keyMonitorAdditionalEquipmentVulcan] = getEquipmentGroupFormData(
      fields[keyMonitorAdditionalEquipmentVulcan]
    );
    result[
      keyMonitorAdditionalEquipmentVulcan
    ] = removeUnusedSelectionsIncludedEquipments(
      result[keyMonitorAdditionalEquipmentVulcan]
    );
  }
  if (fields[keyProtectIncludedEquipmentVulcan]) {
    result[keyProtectIncludedEquipmentVulcan] = getEquipmentGroupFormData(
      fields[keyProtectIncludedEquipmentVulcan]
    );
    result[
      keyProtectIncludedEquipmentVulcan
    ] = removeUnusedSelectionsIncludedEquipments(
      result[keyProtectIncludedEquipmentVulcan]
    );
  }
  if (fields[keyProtectIncludedEquipmentCameraSelectionVulcan]) {
    result[
      keyProtectIncludedEquipmentCameraSelectionVulcan
    ] = getEquipmentGroupFormData(
      fields[keyProtectIncludedEquipmentCameraSelectionVulcan]
    );
    result[
      keyProtectIncludedEquipmentCameraSelectionVulcan
    ] = removeUnusedSelectionsIncludedEquipments(
      result[keyProtectIncludedEquipmentCameraSelectionVulcan]
    );
  }
  if (fields[keyProtectAdditionalEquipmentVulcan]) {
    result[keyProtectAdditionalEquipmentVulcan] = getEquipmentGroupFormData(
      fields[keyProtectAdditionalEquipmentVulcan]
    );
    result[
      keyProtectAdditionalEquipmentVulcan
    ] = removeUnusedSelectionsIncludedEquipments(
      result[keyProtectAdditionalEquipmentVulcan]
    );
  }
  if (fields[keyControlIncludedEquipmentVulcan]) {
    result[keyControlIncludedEquipmentVulcan] = getEquipmentGroupFormData(
      fields[keyControlIncludedEquipmentVulcan]
    );
    result[
      keyControlIncludedEquipmentVulcan
    ] = removeUnusedSelectionsIncludedEquipments(
      result[keyControlIncludedEquipmentVulcan]
    );
  }
  if (fields[keyControlAdditionalEquipmentVulcan]) {
    result[keyControlAdditionalEquipmentVulcan] = getEquipmentGroupFormData(
      fields[keyControlAdditionalEquipmentVulcan]
    );
    result[
      keyControlAdditionalEquipmentVulcan
    ] = removeUnusedSelectionsIncludedEquipments(
      result[keyControlAdditionalEquipmentVulcan]
    );
  }
  if (fields[keyControlIncludedEquipmentCameraSelectionVulcan]) {
    result[
      keyControlIncludedEquipmentCameraSelectionVulcan
    ] = getEquipmentGroupFormData(
      fields[keyControlIncludedEquipmentCameraSelectionVulcan]
    );
    result[
      keyControlIncludedEquipmentCameraSelectionVulcan
    ] = removeUnusedSelectionsIncludedEquipments(
      result[keyControlIncludedEquipmentCameraSelectionVulcan]
    );
  }
  if (fields[smartCameraIncludedEquipmentCameraSelectionVulcan]) {
    result[
      smartCameraIncludedEquipmentCameraSelectionVulcan
    ] = getEquipmentGroupFormData(
      fields[smartCameraIncludedEquipmentCameraSelectionVulcan]
    );
    result[
      smartCameraIncludedEquipmentCameraSelectionVulcan
    ] = removeUnusedSelectionsIncludedEquipments(
      result[smartCameraIncludedEquipmentCameraSelectionVulcan]
    );
  }
  if (fields[smartCameraAdditionalEquipmentVulcan]) {
    result[smartCameraAdditionalEquipmentVulcan] = getEquipmentGroupFormData(
      fields[smartCameraAdditionalEquipmentVulcan]
    );
    result[
      smartCameraAdditionalEquipmentVulcan
    ] = removeUnusedSelectionsIncludedEquipments(
      result[smartCameraAdditionalEquipmentVulcan]
    );
  }
  if (fields[existingEquipmentVulcan]) {
    result[existingEquipmentVulcan] = getEquipmentGroupFormData(
      fields[existingEquipmentVulcan]
    );
    result[existingEquipmentVulcan] = removeUnusedSelectionsIncludedEquipments(
      result[existingEquipmentVulcan]
    );
  }
  if (fields[takeoverEquipmentWired]) {
    result[takeoverEquipmentWired] = getEquipmentGroupFormData(
      fields[takeoverEquipmentWired]
    );
    result[takeoverEquipmentWired] = removeUnusedSelectionsIncludedEquipments(
      result[takeoverEquipmentWired]
    );
  }
  if (fields[takeoverEquipmentWireless]) {
    result[takeoverEquipmentWireless] = getEquipmentGroupFormData(
      fields[takeoverEquipmentWireless]
    );
    result[
      takeoverEquipmentWireless
    ] = removeUnusedSelectionsIncludedEquipments(
      result[takeoverEquipmentWireless]
    );
  }

  if (step === 1) {
    const getFormFieldSummaryData = getFormFieldSummary(
      fields,
      formFieldsForSummary,
      formFieldsCopy,
      orderData
    );

    result.priceSummaryOfAlternateLanguage = getPricingSummary(
      getFormFieldSummaryData || {},
      true,
      redeemOfferData,
      businessOptikTvTableData,
      officePhoneLinesData,
      fibreInternetData,
      copperInternetData,
      tsbData,
      orderData
    );

    result.priceSummary = getPricingSummary(
      fields || {},
      false,
      redeemOfferData,
      businessOptikTvTableData,
      officePhoneLinesData,
      fibreInternetData,
      copperInternetData,
      tsbData,
      orderData
    );

    result.localLanguageIsDifferent = getLocalLanguageIsDifferent(fields);
    result.redeemOfferData = redeemOfferData;
    result.fibreInternetData = fibreInternetData;
    result.copperInternetData = copperInternetData;
    result.tsbData = tsbData;
  }

  return result;
};

export const checkTheOrderIsVulcan = (formFields, yes, no) => {
  let isValidForVulcan = false;
  const serviceAddress = getFieldValue(fieldProvinceVUL, formFields);
  const isVulcanOrder = getFieldValue(fieldIsVulcanOrder, formFields);

  if (vulcanProvinces.includes(serviceAddress) && isVulcanOrder === yes) {
    isValidForVulcan = true;
  }

  if (vulcanProvinces.includes(serviceAddress) && isVulcanOrder === no) {
    isValidForVulcan = false;
  }

  return isValidForVulcan;
};
